/** The MCE event-class API: read, and choose how each event is delivered. */
import * as flatbuffers from "flatbuffers";
import { unwrapEnvelope } from "../../flatbuffers/envelope";
import { MceEventClassBatchT } from "../../flatbuffers/generated/api/mce-event-class-batch";
import {
  MceEventClassList,
  MceEventClassListT,
} from "../../flatbuffers/generated/api/mce-event-class-list";
import { MceEventClassSetT } from "../../flatbuffers/generated/api/mce-event-class-set";
import { Fault } from "../../flatbuffers/generated/fault/fault";
import { EventKeyT } from "../../flatbuffers/generated/mce/event-key";
import { BitMaskMessageT } from "../../flatbuffers/generated/ln2/bit-mask-message";
import { EncodedMessageContent } from "../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageT } from "../../flatbuffers/generated/ln2/message";
import { MessageContent } from "../../flatbuffers/generated/ln2/message-content";
import { ValuedMessageT } from "../../flatbuffers/generated/ln2/valued-message";
import { ElevatorFaultMessageT } from "../../flatbuffers/generated/fault/elevator-fault-message";
import { EscalatorFaultMessageT } from "../../flatbuffers/generated/fault/escalator-fault-message";
import { FaultContent } from "../../flatbuffers/generated/fault/fault-content";
import { FaultMessageT } from "../../flatbuffers/generated/fault/fault-message";
import { FaultVariant } from "../../flatbuffers/generated/fault/fault-variant";
import { NormalFaultT } from "../../flatbuffers/generated/fault/normal-fault";
import { DeviceServiceContent } from "../../flatbuffers/generated/service/device-service-content";
import { ElevatorServiceMessageT } from "../../flatbuffers/generated/service/elevator-service-message";
import { EscalatorServiceMessageT } from "../../flatbuffers/generated/service/escalator-service-message";
import { NormalServiceT } from "../../flatbuffers/generated/service/normal-service";
import { ServiceMessageT } from "../../flatbuffers/generated/service/service-message";
import { ServiceVariant } from "../../flatbuffers/generated/service/service-variant";
import { DeviceStatusContent } from "../../flatbuffers/generated/status/device-status-content";
import { ElevatorStatusMessageT } from "../../flatbuffers/generated/status/elevator-status-message";
import { EscalatorStatusMessageT } from "../../flatbuffers/generated/status/escalator-status-message";
import { NormalStatusT } from "../../flatbuffers/generated/status/normal-status";
import { StatusMessageT } from "../../flatbuffers/generated/status/status-message";
import { StatusVariant } from "../../flatbuffers/generated/status/status-variant";
import { Fill } from "../../flatbuffers/generated/mce/fill";
import { Origin } from "../../flatbuffers/generated/mce/origin";
import { TemplateT } from "../../flatbuffers/generated/mce/template";
import { Service } from "../../flatbuffers/generated/service/service";
import { Status } from "../../flatbuffers/generated/status/status";
import type { DeviceType } from "../../flatbuffers/types/ln2MessageOptions";
import { fetchFlatBufferBinary } from "../flatbufferApi";
import { asString } from "./messages";
import { serversMessage } from "../serverScopeRefusal";
import { refusedServers, MceSaveFailure } from "./saveUpload";

/** The classes an event can be delivered as. */
export type MceEventClass = "fault" | "status" | "service";

/** One change in a bulk action: a class and member to set, or a reset. */
export interface MceEventChange {
  key: EventKeyT;
  template: TemplateT | null;
}

const MAX_LOGGED_TEXT = 2000;
const COULD_NOT_CHANGE =
  "The event could not be changed. Try again. If this continues, click Get log and send the file to technical support.";
const CHANGED_ELSEWHERE =
  "The adapter changed since this list loaded. Reload the list and try again.";
const STATUS_MESSAGES: Record<number, string> = {
  401: "You do not have permission to change MCE events.",
  403: "You do not have permission to change MCE events.",
  412: CHANGED_ELSEWHERE,
  428: CHANGED_ELSEWHERE,
  503: "The server cannot check MCE files right now. Try again in a few minutes.",
};

const ENUMS: Record<MceEventClass, Record<string, string | number>> = {
  fault: Fault,
  status: Status,
  service: Service,
};

/** The picker's options: the generated enum's names, and nothing else. */
export function memberOptions(eventClass: MceEventClass) {
  return Object.entries(ENUMS[eventClass])
    .filter(([, value]) => typeof value === "number")
    .map(([label, value]) => ({ label, value: value as number }));
}

/** The name of a row's origin. */
export const originName = (origin: Origin): string => Origin[origin] ?? "";
/** Whether a row has no profile match and no override. */
export const isUnmappedOrigin = (origin: Origin): boolean => origin === Origin.Unmapped;
/** Whether a row carries an override a reset would remove. */
export const isOverrideOrigin = (origin: Origin): boolean => origin === Origin.Override;

function listUrl(apiBase: string, adapterId: string): string {
  return `${apiBase}/adapters/mce-events/${adapterId}`;
}

function listFrom(bytes: Uint8Array): MceEventClassListT {
  const buffer = new flatbuffers.ByteBuffer(unwrapEnvelope(bytes));
  return MceEventClassList.getRootAsMceEventClassList(buffer).unpack();
}

/** The event path segment: the row's type bits, a dot, and the event id. */
export function eventKeyPath(key: EventKeyT): string {
  return `${key.types}.${key.eventId}`;
}

/**
 * The message a class and member become. The device form follows the adapter's
 * profile; the server keeps the rest of the row's rule, its arity included.
 */
export function pickerValueToTemplate(
  eventClass: MceEventClass,
  member: number,
  device: DeviceType,
): TemplateT {
  const elevator = device === "elevator";
  const inner: Record<MceEventClass, () => [MessageContent, BitMaskMessageT["message"]]> = {
    status: () => [
      MessageContent.Status_StatusMessage,
      elevator
        ? new StatusMessageT(
            DeviceStatusContent.ElevatorStatusMessage,
            new ElevatorStatusMessageT(StatusVariant.NormalStatus, new NormalStatusT(member)),
          )
        : new StatusMessageT(
            DeviceStatusContent.EscalatorStatusMessage,
            new EscalatorStatusMessageT(StatusVariant.NormalStatus, new NormalStatusT(member)),
          ),
    ],
    service: () => [
      MessageContent.Service_ServiceMessage,
      elevator
        ? new ServiceMessageT(
            DeviceServiceContent.ElevatorServiceMessage,
            new ElevatorServiceMessageT(ServiceVariant.NormalService, new NormalServiceT(member)),
          )
        : new ServiceMessageT(
            DeviceServiceContent.EscalatorServiceMessage,
            new EscalatorServiceMessageT(ServiceVariant.NormalService, new NormalServiceT(member)),
          ),
    ],
    fault: () => [
      MessageContent.Fault_FaultMessage,
      elevator
        ? new FaultMessageT(
            FaultContent.ElevatorFaultMessage,
            new ElevatorFaultMessageT(FaultVariant.NormalFault, new NormalFaultT(member)),
          )
        : new FaultMessageT(
            FaultContent.EscalatorFaultMessage,
            new EscalatorFaultMessageT(FaultVariant.NormalFault, new NormalFaultT(member)),
          ),
    ],
  };
  const [content, message] = inner[eventClass]();
  const encoded = new BitMaskMessageT(content, message);
  const valued = new ValuedMessageT(
    new MessageT(EncodedMessageContent.BitMaskMessage, encoded),
    true,
  );
  return new TemplateT(valued, Fill.Active);
}

/** The class a template delivers, or null for a message that is not one an event can be set to. */
export function eventClassOfTemplate(template: TemplateT | null): MceEventClass | null {
  const encoded = template?.msg?.msg?.content;
  if (!encoded) return null;
  switch (encoded.messageType) {
    case MessageContent.Fault_FaultMessage:
      return "fault";
    case MessageContent.Status_StatusMessage:
      return "status";
    case MessageContent.Service_ServiceMessage:
      return "service";
    default:
      return null;
  }
}

/** Read every event of an adapter. Null when it cannot be read. */
export async function fetchEventClasses(
  apiBase: string,
  adapterId: string,
  token: string,
): Promise<MceEventClassListT | null> {
  const bytes = await fetchFlatBufferBinary(listUrl(apiBase, adapterId), token);
  return bytes ? listFrom(bytes) : null;
}

/** Apply one or more changes in one rebuild, with the list's etag as the precondition. */
export async function patchEventClasses(
  apiBase: string,
  adapterId: string,
  token: string,
  etag: string | Uint8Array | null,
  changes: MceEventChange[],
): Promise<{ list: MceEventClassListT } | { failure: MceSaveFailure }> {
  const batch = new MceEventClassBatchT(
    changes
      .filter((change) => change.template)
      .map((change) => new MceEventClassSetT(change.key, change.template)),
    changes.filter((change) => !change.template).map((change) => change.key),
  );
  const builder = new flatbuffers.Builder(1024);
  builder.finish(batch.pack(builder));
  try {
    const response = await fetch(`${apiBase}/adapters/mce-events/${adapterId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/flatbuffers",
        Accept: "application/flatbuffers",
        Authorization: token,
        "If-Match": `"${asString(etag).replace(/"/g, "")}"`,
      },
      body: builder.asUint8Array().slice().buffer,
    });
    const body = new Uint8Array(await response.arrayBuffer());
    if (response.ok) return { list: listFrom(body) };
    const text = new TextDecoder().decode(body);
    const servers = response.status === 409 ? refusedServers(text) : [];
    return {
      failure: {
        message:
          servers.length > 0
            ? serversMessage(servers)
            : (STATUS_MESSAGES[response.status] ?? COULD_NOT_CHANGE),
        detail: `events: HTTP ${response.status}\n${text.slice(0, MAX_LOGGED_TEXT)}`,
      },
    };
  } catch (error) {
    return { failure: { message: COULD_NOT_CHANGE, detail: `events: ${String(error)}` } };
  }
}
